import { signal } from "@preact/signals";
import { getMe, setAuthToken } from "../api/client";
import type { FullAuthResponse, FullMe } from "../api/types";

const TOKEN_KEY = "gunrun.token";

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeToken(value: string | null): void {
  try {
    if (value) localStorage.setItem(TOKEN_KEY, value);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage blocked (private mode): the session lasts until reload.
  }
}

/** Session token (persisted in localStorage "gunrun.token"). */
export const token = signal<string | null>(readToken());
setAuthToken(token.value);

/** Current account; null until boot has authenticated. */
export const me = signal<FullMe | null>(null);

/** Store the result of guest/register/login. */
export function applyAuth(res: FullAuthResponse): void {
  token.value = res.token;
  setAuthToken(res.token);
  writeToken(res.token);
  me.value = res.me;
}

export function setMe(next: FullMe): void {
  me.value = next;
}

/** Forget the session locally (the server session is revoked separately by `logout`). */
export function clearAuth(): void {
  token.value = null;
  setAuthToken(null);
  writeToken(null);
  me.value = null;
}

/** Re-fetch the account (after a match, a loadout save, ...). Keeps the old value on failure. */
export async function refreshMe(): Promise<FullMe> {
  const next = await getMe();
  me.value = next;
  return next;
}

/**
 * The signed-in account for screens. Menu screens only render after boot has authenticated, so a
 * missing account is a programming error. Reading it inside a component subscribes to changes.
 */
export function requireMe(): FullMe {
  const value = me.value;
  if (!value) throw new Error("requireMe: no authenticated account");
  return value;
}
